import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addCollectiveParticipants,
  deleteCollectiveParticipant,
  fetchCollective,
  remindCollectiveParticipant,
  updateCollectiveParticipant,
  updateCollectiveSettings
} from '../../../services/compositionApi';
import usePanneauAccessible from './usePanneauAccessible';
import './CollectivePanel.css';

// Panneau « Mon collectif » (refonte du collectif, phase 2, 2026-10-24 — maquette docs/bookipix-collectif.html).
// Remplace la page /book/:id/collectif et ses trois onglets : UN panneau qui defile, pose sur l'atelier.
// Ordre : resume, Partager (le lien COMMUN d'abord), Invites, Parametres replies.

const aujourdhui = () => new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());
const joursEntre = (de, a) => Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / 86400000);
// Couleurs des pastilles d'initiale : uniquement des jetons du design system, toujours dans le meme ordre.
const TEINTES = ['var(--gold)', 'var(--ok)', 'var(--warn)', 'var(--bad)', 'var(--ink)'];
const TON_STATUT = { invited: 'mute', opened: '', started: 'warn', completed: 'ok' };

export default function CollectiveManagePanel({ book, usedItemIds, onClose, onChanged }) {
  const { t, i18n } = useTranslation('collective');
  const panneauRef = useRef(null);
  const titreRef = useRef(null);
  usePanneauAccessible(panneauRef, onClose, titreRef);

  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [annonce, setAnnonce] = useState('');
  const [copie, setCopie] = useState(false);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [adresses, setAdresses] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [menu, setMenu] = useState(null);
  const [retrait, setRetrait] = useState(null); // { id, photos: bool }
  const [edition, setEdition] = useState(null); // { id, email }
  const [relances, setRelances] = useState({}); // relances faites pendant cette ouverture

  const charger = useCallback(async () => {
    try { setData(await fetchCollective(book.id)); setErreur(''); } catch (err) { setErreur(err.message || t('mine.loadFailed')); }
  }, [book.id, t]);
  useEffect(() => { charger(); }, [charger]);
  const apres = async () => { await charger(); if (onChanged) onChanged(); };
  const dire = (texte) => { setAnnonce(''); setTimeout(() => setAnnonce(texte), 30); };

  // Menu « ··· » : un clic ailleurs le ferme.
  useEffect(() => {
    if (menu === null) return undefined;
    const fermer = (e) => { if (!e.target.closest('.cp-g-menu, .cp-dots')) setMenu(null); };
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, [menu]);

  if (!data) {
    return (
      <Coque panneauRef={panneauRef} titreRef={titreRef} titre={t('mine.title')} onClose={onClose} t={t}>
        <div className="cp-body">{erreur ? <p className="cp-err" role="alert">{erreur}</p> : <p role="status">{t('mine.loading')}</p>}</div>
      </Coque>
    );
  }

  const { settings, participants } = data;
  const total = participants.length;
  const ontContribue = participants.filter((p) => p.status === 'completed' || (p.counts.photos + p.counts.souvenirs) > 0).length;
  const photos = participants.reduce((n, p) => n + p.counts.photos, 0);
  const mots = participants.reduce((n, p) => n + p.counts.souvenirs, 0);
  const pasOuverts = participants.filter((p) => p.status === 'invited').length;
  const restants = settings.deadline ? joursEntre(aujourdhui(), settings.deadline) : null;
  const dateLimite = settings.deadline ? new Date(`${settings.deadline}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : '';
  const lien = settings.collectiveToken ? `${window.location.origin}/invitation/${settings.collectiveToken}` : '';
  const texteInvitation = settings.message || t('panel.messageBase');
  const sujet = settings.eventTitle ? t('mine.mailSubjectEvent', { event: settings.eventTitle }) : t('mine.mailSubject');

  const copier = async () => {
    try { await navigator.clipboard.writeText(lien); } catch (_e) { /* presse-papiers refuse : le lien reste affiche et selectionnable */ }
    setCopie(true);
    dire(t('mine.linkCopied'));
    setTimeout(() => setCopie(false), 2200);
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${texteInvitation}\n\n${lien}`)}`, '_blank', 'noopener');
  const courriel = () => { window.location.href = `mailto:?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(`${texteInvitation}\n\n${lien}`)}`; };

  const inviterParEmail = async () => {
    const liste = adresses.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean);
    if (!liste.length) return;
    setEnvoi(true);
    try {
      await addCollectiveParticipants(book.id, liste, settings.organizerName);
      setAdresses('');
      setAjoutOuvert(false);
      dire(t('mine.invitationsSent', { count: liste.length }));
      await apres();
    } catch (err) { setErreur(err.message || t('mine.actionFailed')); } finally { setEnvoi(false); }
  };

  const relancer = async (p) => {
    try {
      const r = await remindCollectiveParticipant(book.id, p.id);
      setRelances((prec) => ({ ...prec, [p.id]: true }));
      dire(r?.emailSent ? t('mine.reminded', { name: p.name || p.email }) : t('mine.remindNotSent'));
      await apres();
    } catch (err) { setErreur(err.message || t('mine.actionFailed')); }
  };

  const retirer = async () => {
    const p = participants.find((x) => x.id === retrait.id);
    try {
      const r = await deleteCollectiveParticipant(book.id, retrait.id, { retirerPhotos: retrait.photos });
      setRetrait(null);
      dire(r?.removedPhotos ? t('mine.removedWithPhotos', { name: p?.name || p?.email, count: r.removedPhotos }) : t('mine.removed', { name: p?.name || p?.email }));
      await apres();
    } catch (err) { setErreur(err.message || t('mine.actionFailed')); }
  };

  const enregistrerEmail = async () => {
    try {
      await updateCollectiveParticipant(book.id, edition.id, { email: edition.email });
      setEdition(null);
      dire(t('mine.emailSaved'));
      await apres();
    } catch (err) { setErreur(err.message || t('mine.actionFailed')); }
  };

  const copierLienPersonnel = async (p) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/collectif/${p.invite_token}`); } catch (_e) { /* idem */ }
    setMenu(null);
    dire(t('mine.personalLinkCopied', { name: p.name || p.email }));
  };

  const resumeLigne = (p) => {
    const morceaux = [];
    if (p.status === 'invited') morceaux.push(p.email ? t('mine.line.invited') : t('mine.line.invitedNoMail'));
    else if (p.counts.photos + p.counts.souvenirs === 0) morceaux.push(t('mine.line.openedNothing'));
    else {
      if (p.counts.photos) morceaux.push(t('mine.line.photos', { count: p.counts.photos }));
      if (p.counts.souvenirs) morceaux.push(t('mine.line.words', { count: p.counts.souvenirs }));
    }
    if (p.joined_via === 'link') morceaux.push(t('mine.line.viaLink'));
    if (p.last_reminder_sent_at) morceaux.push(t('mine.line.remindedOn', { date: new Date(p.last_reminder_sent_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }) }));
    return morceaux.join(' · ');
  };

  return (
    <Coque panneauRef={panneauRef} titreRef={titreRef} titre={t('mine.title')} onClose={onClose} t={t}>
      <div className="cp-body">
        <p className="cp-sr" role="status" aria-live="polite">{annonce}</p>
        {erreur && <p className="cp-err" role="alert">{erreur}</p>}

        {/* Résumé */}
        <section className="cp-sum" aria-label={t('mine.summaryLabel')}>
          <h3>{settings.eventTitle || data.bookTitle}</h3>
          <p className="cp-sub">
            {settings.isClosed
              ? t('mine.closed', { date: dateLimite })
              : t('mine.deadline', { date: dateLimite, count: Math.max(0, restants ?? 0) })}
          </p>
          <div className="cp-progress" role="progressbar" aria-valuemin={0} aria-valuemax={Math.max(1, total)} aria-valuenow={ontContribue} aria-label={t('mine.progressLabel')}>
            <i style={{ width: `${total ? Math.round((ontContribue / total) * 100) : 0}%` }} />
          </div>
          <div className="cp-stats">
            <span><b>{total}</b> {t('mine.stats.guests', { count: total })}</span>
            <span><b>{ontContribue}</b> {t('mine.stats.contributed', { count: ontContribue })}</span>
            <span><b>{photos}</b> {t('mine.stats.photos', { count: photos })} · <b>{mots}</b> {t('mine.stats.words', { count: mots })}</span>
            {pasOuverts > 0 && <span><b>{pasOuverts}</b> {t('mine.stats.notOpened', { count: pasOuverts })}</span>}
          </div>
        </section>

        {/* Partager */}
        <section aria-labelledby="cp-partager">
          <div className="cp-lab" id="cp-partager">{t('mine.share')}</div>
          <div className="cp-share">
            <button type="button" className="btn btn-primary" onClick={copier} disabled={!lien}>{copie ? t('mine.linkCopied') : t('mine.copyLink')}</button>
            <button type="button" className="cp-ib" onClick={whatsapp} disabled={!lien} aria-label={t('mine.shareWhatsapp')} title={t('mine.shareWhatsapp')}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4V5z" /></svg>
            </button>
            <button type="button" className="cp-ib" onClick={courriel} disabled={!lien} aria-label={t('mine.shareEmail')} title={t('mine.shareEmail')}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
            </button>
          </div>
          {lien && <div className="cp-linkbox"><span>{lien.replace(/^https?:\/\//, '')}</span></div>}
          <p className="cp-hint">{t('mine.shareHint')}</p>
          <button type="button" className="cp-linkbtn" aria-expanded={ajoutOuvert} onClick={() => setAjoutOuvert((v) => !v)}>{t('mine.inviteByEmail')}</button>
          {ajoutOuvert && (
            <div className="cp-addf">
              <textarea className="cp-input" rows={2} value={adresses} onChange={(e) => setAdresses(e.target.value)} placeholder="marc@email.fr, ines@email.fr" aria-label={t('mine.emailsLabel')} />
              <span className="cp-hint">{t('mine.emailsHint')}</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={inviterParEmail} disabled={envoi || !adresses.trim()}>{envoi ? t('mine.sending') : t('mine.sendInvitations')}</button>
            </div>
          )}
        </section>

        {/* Invités */}
        <section aria-labelledby="cp-invites">
          <div className="cp-lab" id="cp-invites">{t('mine.guests', { count: total })}</div>
          {total === 0 && <p className="cp-hint">{t('mine.noGuests')}</p>}
          <ul className="cp-gl">
            {participants.map((p, index) => {
              const nom = p.name || p.email || t('mine.noName');
              const nonPlacees = (p.counts.photoIds || []).filter((id) => !(usedItemIds && usedItemIds.has(id))).length;
              const peutRelancer = p.status !== 'completed' && Boolean(p.email);
              const relanceAujourdhui = relances[p.id] || (p.last_reminder_sent_at && new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date(p.last_reminder_sent_at)) === aujourdhui());
              return (
                <li key={p.id} className="cp-g">
                  <div className="cp-g-row">
                    <span className="cp-av" style={{ background: TEINTES[index % TEINTES.length] }} aria-hidden="true">{nom.charAt(0).toUpperCase()}</span>
                    <div className="cp-g-id">
                      <div className="cp-g-nm">{nom} <span className={`cp-tag ${TON_STATUT[p.status] || ''}`}><i aria-hidden="true" />{t(`mine.status.${p.status || 'invited'}`)}</span></div>
                      <div className="cp-g-mt">{resumeLigne(p)}</div>
                    </div>
                    {peutRelancer && (
                      <button type="button" className="cp-linkbtn" onClick={() => relancer(p)} disabled={relanceAujourdhui}>
                        {relanceAujourdhui ? t('mine.reminded_short') : t('mine.remind')}
                      </button>
                    )}
                    <button type="button" className="cp-dots" aria-haspopup="true" aria-expanded={menu === p.id} aria-label={t('mine.actionsFor', { name: nom })} onClick={() => setMenu(menu === p.id ? null : p.id)}>···</button>
                    {menu === p.id && (
                      <div className="cp-g-menu" role="menu">
                        <button type="button" role="menuitem" onClick={() => copierLienPersonnel(p)}>{t('mine.copyPersonalLink')}</button>
                        <button type="button" role="menuitem" onClick={() => { setEdition({ id: p.id, email: p.email || '' }); setMenu(null); }}>{p.email ? t('mine.editEmail') : t('mine.addEmail')}</button>
                        <button type="button" role="menuitem" className="is-del" onClick={() => { setRetrait({ id: p.id, photos: false }); setMenu(null); }}>{t('mine.remove')}</button>
                      </div>
                    )}
                  </div>
                  {edition?.id === p.id && (
                    <div className="cp-inline">
                      <input className="cp-input" type="email" value={edition.email} onChange={(e) => setEdition({ ...edition, email: e.target.value })} aria-label={t('mine.emailOf', { name: nom })} />
                      <div className="cp-row">
                        <button type="button" className="btn btn-primary btn-sm" onClick={enregistrerEmail}>{t('mine.save')}</button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setEdition(null)}>{t('mine.cancel')}</button>
                      </div>
                    </div>
                  )}
                  {retrait?.id === p.id && (
                    <div className="cp-confirm" role="alertdialog" aria-label={t('mine.removeTitle', { name: nom })}>
                      <b>{t('mine.removeTitle', { name: nom })}</b>
                      <span>{t('mine.removeBody')}</span>
                      {nonPlacees > 0 && (
                        <label className="cp-check">
                          <input type="checkbox" checked={retrait.photos} onChange={(e) => setRetrait({ ...retrait, photos: e.target.checked })} />
                          <span>{t('mine.removePhotosToo', { count: nonPlacees })}<small>{t('mine.removePhotosHint')}</small></span>
                        </label>
                      )}
                      <div className="cp-row">
                        <button type="button" className="btn btn-sm cp-danger" onClick={retirer}>{t('mine.removeYes')}</button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setRetrait(null)}>{t('mine.keep')}</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        <Parametres book={book} settings={settings} onSaved={async () => { dire(t('mine.settingsSaved')); await apres(); }} t={t} />
      </div>
    </Coque>
  );
}

function Coque({ panneauRef, titreRef, titre, onClose, children, t }) {
  return (
    <>
      <button type="button" className="cp-scrim" aria-label={t('panel.close')} onClick={onClose} tabIndex={-1} />
      <div className="cp-panel" role="dialog" aria-modal="true" aria-labelledby="cp-mine-titre" ref={panneauRef}>
        <div className="cp-head">
          <h2 id="cp-mine-titre" ref={titreRef} tabIndex={-1}>{titre}</h2>
          <button type="button" className="cp-x" onClick={onClose} aria-label={t('panel.close')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

// Paramètres repliés : les mêmes champs que l'activation.
function Parametres({ book, settings, onSaved, t }) {
  const [nom, setNom] = useState(settings.organizerName || '');
  const [occasion, setOccasion] = useState(settings.eventTitle || '');
  const [message, setMessage] = useState(settings.message || '');
  const [dateLimite, setDateLimite] = useState(settings.deadline || '');
  const [relances, setRelances] = useState(Boolean(settings.remindersEnabled));
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const enregistrer = async (event) => {
    event.preventDefault();
    if (nom.trim().length < 2) { setErreur(t('panel.errors.name')); return; }
    if (!dateLimite) { setErreur(t('panel.errors.deadlineMissing')); return; }
    setEnCours(true);
    setErreur('');
    try {
      await updateCollectiveSettings(book.id, {
        organizerName: nom.trim(), eventTitle: occasion.trim(), message: message.trim(), deadline: dateLimite, remindersEnabled: relances, reminderDaysBefore: settings.reminderDaysBefore || [7, 2]
      });
      await onSaved();
    } catch (err) { setErreur(err.message || t('mine.actionFailed')); } finally { setEnCours(false); }
  };

  return (
    <details className="cp-set">
      <summary>{t('mine.settings')}</summary>
      <form className="cp-set-in" onSubmit={enregistrer} noValidate>
        <label className="cp-field">{t('panel.nameLabel')}<input className="cp-input" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} /></label>
        <label className="cp-field">{t('panel.occasionLabel')}<input className="cp-input" value={occasion} onChange={(e) => setOccasion(e.target.value)} maxLength={120} placeholder={t('panel.occasionPlaceholder')} /></label>
        <label className="cp-field">{t('panel.messageLabel')}<textarea className="cp-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} /></label>
        <label className="cp-field">{t('panel.deadlineLabel')}<input className="cp-input" type="date" value={dateLimite} onChange={(e) => setDateLimite(e.target.value)} /></label>
        <label className="cp-check">
          <input type="checkbox" checked={relances} onChange={(e) => setRelances(e.target.checked)} />
          <span>{t('panel.remindersLabel')}<small>{t('panel.remindersHonest')}</small></span>
        </label>
        {erreur && <p className="cp-err" role="alert">{erreur}</p>}
        <button type="submit" className="btn btn-primary btn-sm" disabled={enCours}>{enCours ? t('mine.saving') : t('mine.save')}</button>
      </form>
    </details>
  );
}
